import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit, ViewChild } from '@angular/core';
import {
    ControlContainer,
    FormArray,
    FormBuilder,
    FormControl,
    FormGroup,
    FormGroupDirective,
    Validators,
} from '@angular/forms';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { Router } from '@angular/router';
import { AuthService } from '@klickdata/core/auth';
import { ConfigService } from '@klickdata/core/config';
import { FormHelper } from '@klickdata/core/form';
import { CustomValidatorService } from '@klickdata/core/form/src/validators/custom-validator.service';
import { MessageService } from '@klickdata/core/message';
import { MessageErrorComponent } from '@klickdata/core/message/src/message-error/message-error.component';
import {
    AppScope,
    Resource,
    ResourceCategoryService,
    ResourceData,
    ResourceService,
    ResourceTypes,
} from '@klickdata/core/resource';
import { ResourceBuilderComposerDirective } from '@klickdata/core/resource/src/resource-builder-composer-directive';
import { Utils } from '@klickdata/core/util';
import moment from 'moment';
import { Observable, of } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { SelectFilterOption } from './../../../core/table/src/table-filter/filter';
import { EventManagerCoreSecondaryControlsComponent } from './event-manager-core-secondary-controls/event-manager-core-secondary-controls.component';
import { IdleStateTrackerService } from '@klickdata/core/application';

@Component({
    selector: 'app-event-manager-core',
    templateUrl: './event-manager-core.component.html',
    styleUrls: ['./event-manager-core.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    viewProviders: [{ provide: ControlContainer, useExisting: FormGroupDirective }],
})
export class EventManagerCoreComponent extends ResourceBuilderComposerDirective implements OnInit {
    @ViewChild(EventManagerCoreSecondaryControlsComponent, { static: false })
    secondaryControls?: EventManagerCoreSecondaryControlsComponent;
    @Input() active: boolean;
    typeId = ResourceTypes.EVENT;
    public startDate: Date | null;
    public endDate: Date | null;
    ResourceTypes = ResourceTypes;
    public selection: number[] = [];
    public notSetYet = new FormControl(true);
    public eventTypes: { value: ResourceTypes; label: string; eventTypeId: number }[];
    public defaultResourceOnlineEventId: number;
    Utils: Utils;
    AppScope = AppScope;
    public mainEventTypeId = new FormControl(1);
    public timezones: SelectFilterOption[];

    get resourceBuilder() {
        return null;
    }

    constructor(
        protected parentFormDirective: FormGroupDirective,
        protected auth: AuthService,
        protected categoryService: ResourceCategoryService,
        protected messageService: MessageService,
        protected resourceService: ResourceService,
        protected configService: ConfigService,
        protected fb: FormBuilder,
        protected router: Router,
        protected bottomSheet: MatBottomSheet,
        protected validatorService: CustomValidatorService,
        protected cdRef: ChangeDetectorRef,
        protected idleStateTracker: IdleStateTrackerService
    ) {
        super(
            resourceService,
            categoryService,
            auth,
            parentFormDirective,
            messageService,
            router,
            bottomSheet,
            idleStateTracker
        );
        this.eventTypes = ResourceTypes.getEventTypes();
        this.defaultResourceOnlineEventId = this.configService.config.defaultResourceOnlineEventId;
        this.timezones = Utils.getWorldTimeZones();
    }

    ngOnInit(): void {
        super.ngOnInit();
        this.resourceForm.addControl('agenda', new FormControl(''));
        this.resourceForm.addControl('time_zone', new FormControl('CET'));
        this.resourceForm.addControl('type_id', new FormControl(ResourceTypes.getEventTypes()[0].value));

        this.resourceForm.addControl(
            'address',
            this.fb.group({
                street: [],
                other: [],
                coordinates: this.fb.group({
                    langitude: [],
                    latitude: [],
                }),
            })
        );
        this.resourceForm.addControl(
            'communication',
            this.fb.group({
                link: this.fb.group({
                    value: [],
                    metadata: this.fb.group({
                        password: [],
                        other: [],
                    }),
                }),
            })
        );
        if (this.resourceForm.get('publish')) {
            this.resourceForm
                .get('publish')
                .valueChanges.pipe(takeUntil(this.destroy))
                .subscribe((publish) => {
                    this.resourceForm.get('agenda').setValidators(publish ? [Validators.required] : []);
                    this.resourceForm.get('bullets').setValidators(publish ? [Validators.required] : []);
                });
        }

        this.notSetYet.valueChanges
            .pipe(
                takeUntil(this.destroy),
                filter((val) => !!val)
            )
            .subscribe(() => {
                this.eventOccasions.clear();
                this.eventOccasions.markAsDirty();
            });

        this.mainEventTypeId.valueChanges.pipe(takeUntil(this.destroy)).subscribe((eventTypeId) => {
            const onsideTypes = this.eventTypes.filter((type) => type.eventTypeId == 1).map((type) => type.value);
            const typeIdControl = this.resourceForm.get('type_id');
            if (eventTypeId == 1) {
                if (!onsideTypes.includes(this.resourceForm.value.type_id)) {
                    typeIdControl.patchValue(onsideTypes[0]);
                    typeIdControl.markAsDirty();
                }
            } else {
                typeIdControl.patchValue(ResourceTypes.WEBINAR);
                typeIdControl.markAsDirty();
            }
        });
        this.eventOccasions?.valueChanges
            .pipe(
                takeUntil(this.destroy),
                filter((value) => !!value.length)
            )
            .subscribe((value) => {
                this.notSetYet.patchValue(false);
            });

        this.resourceForm.valueChanges
            .pipe(
                takeUntil(this.destroy),
                filter(() => this.resourceForm.dirty)
            )
            .subscribe(() => {
                const dirtyControls = Object.keys(this.resourceForm.controls).filter(
                    (key) => this.resourceForm.get(key)?.dirty
                );
                console.log('Dirty form controls:', dirtyControls);
            });
    }

    onResourceChanged(created: boolean) {
        this.updateEventOccasions();
        this.mainEventTypeId.patchValue(
            this.eventTypes.find((type) => type.value == this.resource?.type_id)?.eventTypeId || 1,
            { emitEvent: false }
        );
    }
    updateEventOccasions() {
        const resOccasionsLength =
            this.resource?.collaboration?.enroll_approval_required?.metadata?.occasion_dates?.length;
        if (!resOccasionsLength) {
            this.eventOccasions.clear();
        }
        this.notSetYet.patchValue(!resOccasionsLength, { emitEvent: false });

        this.cdRef.markForCheck();
    }
    filteredEventTypes() {
        return this.eventTypes.filter((type) => type.eventTypeId === this.mainEventTypeId.value);
    }
    get resourceIsAlwaysOpen() {
        return this.resourceForm.get('collaboration.enroll_approval_required.metadata.occasion_always_open').value;
    }
    public onSubmit() {
        this.loading.emit(true);
        this.checkSubmitValid();
    }
    goToLink(url: string) {
        if (Utils.isUrl(url)) {
            window.open(url, '_blank');
        }
    }
    addDate() {
        this.notSetYet.setValue(false, { emitEvent: false });
        this.secondaryControls?.resourceAccessAndAvailability.addDate();
    }
    removeDate(i: number) {
        this.secondaryControls?.resourceAccessAndAvailability.removeDate(i);
    }
    get eventOccasions(): FormArray {
        return this.resourceForm.get('collaboration.enroll_approval_required.metadata.occasion_dates') as FormArray;
    }
    onHandleResAccessPerm($event: { permType: string; value: any }) {
        this.editResourceAccessPermision($event.permType, $event.value)
            .pipe(takeUntil(this.destroy))
            .subscribe(() => this.cdRef.markForCheck());
    }
    checkSubmitValid() {
        if (this.resourceForm.invalid) {
            if (this.resourceForm.get('collaboration.enroll_approval_required.metadata.occasion_dates').invalid) {
                this.openMessage(MessageErrorComponent, $localize`Errors in enrollment periods`);
                this.loading.emit(false);
                return of(false);
            }
            this.openMessage(
                MessageErrorComponent,
                $localize`${FormHelper.formatInvalidForm(this.resourceForm, this.formats)} is missing`
            );
            this.loading.emit(false);
            return of(false);
        }
        if (this.publish) {
            // if (!this.eventOccasions.length && !this.notSetYet.value) {
            //     this.openMessage(
            //         MessageErrorComponent,
            //         $localize`You need to set the date before you publish your event.`
            //     );
            //     this.loading.emit(false);
            //     return of(false);
            // }
        }

        super.performResSubmit();
        return of(true);
    }

    private checkForTypeId(data: ResourceData): ResourceData {
        return this.resourceForm.get('type_id').dirty
            ? { ...data, ...{ type_id: this.resourceForm.value.type_id } }
            : data;
    }

    public prepareForm(): Observable<ResourceData> {
        const extras = {
            examination: this.resourceForm.value.examination,
            duration: moment.duration(this.resourceForm.value.duration).asSeconds(),
            time_limit: moment.duration(this.resourceForm.value.time_limit).asSeconds(),
        };

        const data = this.resource
            ? this.resource.getData({ ...this.resourceForm.value, ...extras })
            : new Resource({ ...this.resourceForm.value, ...extras }).getData();

        if (this.resourceForm.value.show_publisher_logo === -1) {
            data.show_publisher_logo = null;
        }
        if (this.resourceForm.get('type_id').dirty) {
            data.type_id = this.resourceForm.value.type_id;
        }
        data.collaboration = this.handleCollaboration();
        return of(this.checkForTypeId(data));
    }
    handleCollaboration() {
        const collabForm = this.resourceForm.get('collaboration') as FormGroup;
        const collabValues = (this.resourceForm.get('collaboration') as FormGroup).value;
        // const alwaysOpenOccasion =
        //     this.resourceForm.value.collaboration.enroll_approval_required?.metadata?.occasion_always_open;
        // if (alwaysOpenOccasion) {
        //     this.resourceForm.get('collaboration.enroll_approval_required.metadata.occasion_always_open').markAsDirty();
        // }
        // Object.keys(collabForm.controls).forEach((key) => {
        //     const control = collabForm.get(key);
        //     if (control.pristine) {
        //         delete collabValues[key];
        //     }
        // });

        const occasionDates = collabValues.enroll_approval_required?.metadata?.occasion_dates;
        if (occasionDates?.length) {
            occasionDates.forEach((date) => {
                date.total_seats = date.unlimited_seats ? null : date.total_seats;
                delete date.unlimited_seats;
            });
        }
        // console.log(collabValues, 'course collabValues');
        // console.log(collabForm, 'course collabForm');
        return collabValues;
    }
    public onTabSelectionChange(activeIndex: number) {}
    public getResKeysValidaty(): { [key: string]: string | boolean }[] {
        return [
            {
                key: 'title',
                title: $localize`Event title`,
                hasValidValue: this.resourceForm.get('title').valid,
                mandatory: true,
            },
            {
                key: 'language_id',
                title: $localize`Event language`,
                hasValidValue: this.resourceForm.get('language_id').valid,
                mandatory: true,
            },
            {
                key: 'category_ids',
                title: $localize`Event categories`,
                hasValidValue: this.resourceForm.get('category_ids').valid,
                mandatory: true,
            },
            // {
            //     key: 'items',
            //     title: $localize`Event structure`,
            //     hasValidValue: this.itemsData.length > 0,
            //     mandatory: true,
            // },
            {
                key: 'media_id',
                title: $localize`Event image`,
                hasValidValue: this.resourceForm.value.media_id,
                mandatory: false,
            },
            {
                key: 'tag_ids',
                title: $localize`Event tags`,
                hasValidValue: this.resourceForm.value.tag_ids?.length,
                mandatory: false,
            },
            {
                key: 'bullets',
                title: $localize`Event Summary`,
                hasValidValue: this.resourceForm.value.bullets,
                mandatory: true,
                info: $localize`The short summary of this event that will be displayed udner the title (max 256 characters).`,
            },
            // {
            //     key: 'manager_ids',
            //     title: $localize`Event tutor`,
            //     hasValidValue: this.resourceForm.value.manager_ids?.length,
            //     mandatory: true,
            //     info: $localize`The educator or organizer of the event.`,
            // },
            {
                key: 'agenda',
                title: $localize`Event agenda`,
                hasValidValue: this.resourceForm.value.agenda,
                mandatory: false,
                info: $localize`List the subjects covered in the event. The content in bullets for the lessons, modules, and classes. Suitable for onsite teaching, classroom, or webinar audience. In short, a list of what you will learn.`,
            },
            {
                key: 'availability',
                title: $localize`Event date`,
                hasValidValue: this.eventOccasions?.length || this.notSetYet.value,
                mandatory: true,
                info: $localize`The time period where event will be available for the authorized learners.`,
            },
            // {
            //     key: 'practice_document',
            //     title: $localize`Exercise Documents`,
            //     hasValidValue: this.resourceForm.value.practice_document,
            //     mandatory: false,
            //     info: $localize`Upload exercise document that belongs to the event for practice. It can be a documents pre-event, during event or post the event.`,
            // },
            {
                key: 'description',
                title: $localize`Event description`,
                info: $localize`A description of the event that will be visible befor learners take the event.`,
                hasValidValue: this.resourceForm.value.description,
                mandatory: false,
            },
            {
                key: 'instructions',
                title: $localize`Event instructions`,
                hasValidValue: this.resourceForm.value.instructions,
                mandatory: false,
                info: $localize`Other information that is relevant to this test.`,
            },
            {
                key: 'audience',
                title: $localize`Event audience`,
                hasValidValue: this.resourceForm.value.audience,
                mandatory: false,
                info: $localize`Who is the target for the Event? The person or group who are the learners who will benefit from the knowledge covered in this event.`,
            },
            {
                key: 'goal',
                title: $localize`Event goal`,
                hasValidValue: this.resourceForm.value.goal,
                mandatory: false,
                info: $localize`The goal of this event.`,
            },

            {
                key: 'examination',
                title: $localize`End reward`,
                hasValidValue: this.resourceForm.get('examination')?.value.type,
                mandatory: false,
                info: $localize`Set the reward for this Event. When you have a Final Test in the Event; the learner will receive either a Participation Certificate, a Diploma or a Certification.`,
            },
            {
                key: 'time_limit',
                title: $localize`Play time`,
                hasValidValue: this.resourceForm.value.time_limit,
                mandatory: false,
                info: $localize`Event play time`,
            },
            {
                key: 'pre_skills',
                title: $localize`Pre skills`,
                hasValidValue: this.resourceForm.value.pre_skills,
                mandatory: false,
                info: $localize`The prerequisite knowledge that would be required from the participant prior to the course or the event.`,
            },
            {
                key: 'episode',
                title: $localize`Occasions`,
                hasValidValue: this.resourceForm.value.episode,
                mandatory: false,
                info: $localize`The event time on a calendar when onsite, webinar, classroom, or synchronous teaching.`,
            },
            {
                key: 'duration',
                title: $localize`Duration`,
                hasValidValue: this.resourceForm.value.duration,
                mandatory: false,
                info: $localize`Event duration`,
            },
            {
                key: 'other_info',
                title: $localize`Other information`,
                hasValidValue: this.resourceForm.value.other_info,
                mandatory: false,
                info: $localize`Here you can add other information needed for the users who will take this event`,
            },
            // {
            //     key: 'bg_img',
            //     title: $localize`Background image`,
            //     hasValidValue: this.resourceForm.get('screenshots')?.value.bg_img,
            //     mandatory: false,
            //     info: $localize`Background for the event in the event details page.`,
            // },
            // {
            //     key: 'screenshot',
            //     title: $localize`Screenshot image`,
            //     hasValidValue: this.resourceForm.get('screenshots')?.value.screenshot,
            //     mandatory: false,
            //     info: $localize`A thumbnail from the video (or a homepage) This image is a Screenshot from the creator to be uploaded.`,
            // },
            // {
            //     key: 'images',
            //     title: $localize`Instructor`,
            //     hasValidValue:
            //         (this.resourceForm.get('instructor')?.value.images &&
            //             this.resourceForm.get('instructor')?.value.images.length > 0) ||
            //         this.resourceForm.get('instructor')?.value.name,
            //     mandatory: false,
            //     info: $localize`The one who explain and teach in the Course. Can be the narrator in the video on screen or voice over. In the film industry: This will be equal to Actor `,
            // },
            // {
            //     key: 'educator_ids',
            //     title: $localize`Educators`,
            //     hasValidValue: this.resourceForm.value.educator_ids?.length,
            //     mandatory: false,
            //     info: $localize`Enter the educators of this event.`,
            // },

            {
                key: 'promo_code',
                title: $localize`Promo code`,
                hasValidValue: this.resourceForm.value.promo_code,
                mandatory: false,
                info: $localize`The event promo code`,
            },
            {
                key: 'article_code',
                title: $localize`SKU/ Article number`,
                hasValidValue: this.resourceForm.value.article_code && this.resourceForm.get('article_code').valid,
                mandatory: false,
                info: $localize`Event code is a unique field, autogenerated when empty.`,
            },
            {
                key: 'subtitles',
                title: $localize`Subtitle languages`,
                hasValidValue: this.resourceForm.value.subtitles,
                mandatory: false,
                info: $localize`If Subtitles are availible in the video for exampel ; Info Copied from TED or YouTube`,
            },
            {
                key: 'total_seats',
                title: $localize`Event seats`,
                hasValidValue: true,
                mandatory: false,
                info: $localize`Total seats can be unlimited or specified`,
            },
            {
                key: 'extra_seats',
                title: $localize`Extra seats`,
                hasValidValue: this.resourceForm.value.extra_seats,
                mandatory: false,
                info: $localize`Extra seats activated when seats are full.`,
            },
            {
                key: 'copyrights',
                title: $localize`Copyrights`,
                hasValidValue: this.resourceForm.value.copyrights,
                mandatory: false,
                info: $localize`Set event copyrights`,
            },
            {
                key: 'priority',
                title: $localize`Priority`,
                hasValidValue: this.resourceForm.value.priority,
                mandatory: false,
                info: $localize`Set event priority`,
            },
        ];
    }
    public canDeactivate(): boolean {
        return super.canDeactivate();
    }
}
